import React, { useState } from "react";
import { FaStar } from "react-icons/fa";
import { LuPencil } from "react-icons/lu";
import { useParams } from "react-router-dom";

import { Card } from "../../../../../../../../components/card/Card";
import { Avatar } from "../../../../../../../../components/newUI/avatar";
import { Button } from "../../../../../../../../components/newUI/button";
import { getLinkedFirm } from "../../../../../../../../sessionStorage/sessionStorage";
import { useGetContactQuery } from "../../../../../../../../slices/ContactsSlice";
import { PortalInfoRow } from "../../../../../../../clientPortal/PortalInfoRow";
import { useFilledCustomFields } from "../../../../../../../customFields/useFilledCustomFields";
import { useFirmRegion } from "../../../../../../../region/useFirmRegion";
import { useClientEmailCompose } from "../../../../../../clientEmail/useClientEmailCompose";
import { EditContactModal } from "../../../../modal/EditContact.modal";

type InfoRowProps = {
  label: string;
  children: React.ReactNode;
  withBorder?: boolean;
};

const InfoRow = ({ label, children, withBorder = true }: InfoRowProps) => (
  <div
    className={`flex min-w-0 max-w-full gap-[13px] py-3.5 ${
      withBorder ? "border-t border-[#F0F1F4]" : ""
    }`}>
    <div className={"min-w-0 flex-1"}>
      <div className={"mb-1 text-[12.5px] font-bold text-[#8A92A3]"}>
        {label}
      </div>
      <div
        className={
          "text-[14px] font-semibold leading-[1.45] text-[#232A38] [overflow-wrap:anywhere]"
        }>
        {children}
      </div>
    </div>
  </div>
);

const getAddressText = (
  addresses:
    | Array<{
        line1?: string | null;
        city?: string | null;
        region?: string | null;
        postalCode?: string | null;
      }>
    | undefined,
) =>
  addresses
    ?.map((item) =>
      [
        item.line1,
        [item.city, item.region].filter(Boolean).join(", "),
        item.postalCode,
      ]
        .filter(Boolean)
        .join("\n"),
    )
    .filter(Boolean)
    .join("\n\n") || "";

export const ContactInfo = () => {
  const region = useFirmRegion();
  const { customerId } = useParams();

  const { data: contact } = useGetContactQuery({
    customerId: customerId || "",
    orgId: getLinkedFirm()?.orgId || "",
  });

  const customFieldRows = useFilledCustomFields(customerId, "individual");
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const { openCompose, composeModal } = useClientEmailCompose();

  const fullName = `${contact?.firstName || ""} ${contact?.middleName || ""} ${
    contact?.lastName || ""
  }`
    .replace(/\s+/g, " ")
    .trim();
  const displayName = contact?.displayName || fullName;
  const addressText = getAddressText(contact?.addresses);

  return (
    <Card extraClasses={"overflow-hidden"}>
      <div className={"flex min-w-0 flex-col p-6 pb-5"}>
        <div className={"mb-4 flex items-center justify-between gap-2"}>
          <div className={"flex min-w-0 items-center gap-3.5"}>
            <Avatar
              id={contact?.contactId || customerId || fullName || "contact"}
              fullName={fullName}
              size={"sm"}
              avatarClassNames={"h-9 w-9 shrink-0 text-[13px] font-bold"}
            />
            <div className={"min-w-0 flex-1"}>
              <div
                className={
                  "text-[19px] font-bold leading-[1.2] tracking-[-0.35px] text-[#1A2030] [overflow-wrap:break-word]"
                }>
                {fullName || "-"}
              </div>
            </div>
          </div>
          <Button
            type={"button"}
            variant={"outline"}
            size={"sm"}
            autoWidth
            leftIcon={<LuPencil size={13} />}
            className={
              " h-[34px] shrink-0 rounded-[9px] border-[#E4E7EC] px-3.5 text-[12.5px] font-semibold text-[#4A5262] hover:border-[#D3D8E0] hover:bg-[#F7F8FA] hover:text-[#1F2433]"
            }
            onClick={() => setIsOpenEdit(true)}>
            Edit
          </Button>
        </div>

        <PortalInfoRow customerId={customerId} />

        <div className={"mx-[-24px] h-[463px] overflow-y-auto px-6"}>
          <InfoRow label={"Display name"} withBorder={false}>
            {displayName || "-"}
          </InfoRow>
          {contact?.socialIdentificationNumber && (
            <InfoRow label={region.personalId.label}>
              {contact.socialIdentificationNumber}
            </InfoRow>
          )}
          {contact?.emails?.[0]?.emailAddress && (
            <InfoRow label={"Email"}>
              <div className={"flex flex-col gap-2"}>
                {contact.emails.map((item) => (
                  <button
                    type={"button"}
                    title={`Write an email to ${item.emailAddress || ""}`}
                    className={
                      "inline-flex max-w-full items-center gap-1 text-left text-[14px] font-semibold leading-[1.45] text-[#6B4EFF] [overflow-wrap:anywhere] hover:underline"
                    }
                    key={item.id}
                    onClick={() =>
                      openCompose({
                        email: item.emailAddress,
                        customerId,
                        name: fullName,
                        firstName: contact?.firstName,
                        lastName: contact?.lastName,
                      })
                    }>
                    <span className={"min-w-0 [overflow-wrap:anywhere]"}>
                      {item.emailAddress || "-"}
                    </span>
                    {item.isPrimaryEmail && (
                      <FaStar
                        size={12}
                        className={"shrink-0 text-[#F5A524]"}
                        title={"Primary email"}
                      />
                    )}
                  </button>
                ))}
              </div>
            </InfoRow>
          )}
          {contact?.phoneNumbers?.[0]?.number && (
            <InfoRow label={"Phone"}>
              <div className={"flex flex-col gap-2"}>
                {contact.phoneNumbers.map((item) => (
                  <div key={item.id} className={"[overflow-wrap:anywhere]"}>
                    {item.phoneNumberTypeEnum?.phoneNumberType
                      ? `${item.phoneNumberTypeEnum.phoneNumberType}: `
                      : ""}
                    {item.number}
                  </div>
                ))}
              </div>
            </InfoRow>
          )}
          {customFieldRows.map((field) => (
            <InfoRow key={field.id} label={field.label}>
              <span
                className={
                  "block whitespace-pre-wrap [overflow-wrap:anywhere]"
                }>
                {field.value}
              </span>
            </InfoRow>
          ))}
          <InfoRow label={"Contact type"}>
            {contact?.contactType || "-"}
          </InfoRow>
          {!!addressText && (
            <InfoRow label={"Address"}>
              <span
                className={
                  "block whitespace-pre-wrap [overflow-wrap:anywhere]"
                }>
                {addressText}
              </span>
            </InfoRow>
          )}
          {contact?.additionalInfo && (
            <InfoRow label={"Additional info"}>
              <span
                className={
                  "block whitespace-pre-wrap [overflow-wrap:anywhere]"
                }>
                {contact.additionalInfo}
              </span>
            </InfoRow>
          )}
        </div>
        {composeModal}
        {isOpenEdit && contact && (
          <EditContactModal
            isOpen={isOpenEdit}
            contact={contact}
            onClose={() => setIsOpenEdit(false)}
          />
        )}
      </div>
    </Card>
  );
};
